import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Text,
  Flex,
  Icon,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import ReportList from "./report-list";
import { FILE_TYPE, PAGINATION_DEFAULTS } from "@root/constants";
import { useGlobalContext } from "@root/context/global-context";
import { MaterialIcon } from "@root/assets/icons/materialicon";
import { FolderIcon } from "@root/assets/icons/foldericon";
import { Paginate } from "@root/components/pagination";
import ListIcon from "@root/assets/icons/listicon";
import { useMainChatContext } from "@root/context/kps-chat-context";

interface ReportSectionProps {}

const ReportSection: React.FC<ReportSectionProps> = () => {
  const { t } = useTranslation("common");
  const {
    getReports,
    reports,
    reportsLoading,
    reportCount,
    getReportsExtraction,
    reportsExtraction,
    extractionReportsLoading, // Add this from context
    reportCountExtraction,
  } = useGlobalContext();
  const { review, setReview } = useMainChatContext();

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPageNo] = useState(1);

  const tabItems = useMemo(
    () => [
      {
        label: t("reports.permitComplianceVerification"),
        icon: ListIcon,
        type: FILE_TYPE.EXTRACTION_REVIEW,
      },
      {
        label: t("reports.reviewOpinionExtraction"),
        icon: FolderIcon,
        type: FILE_TYPE.EXCEL_AUTO_COMPLETE,
      },
      {
        label: t("reports.materialSpecificationComparison"),
        icon: MaterialIcon,
        type: FILE_TYPE.FINISHING_MATERIAL_REQUEST,
      },
    ],
    [t]
  );

  // Check if current tab is extraction
  const isExtractionTab =
    tabItems[activeTab].type === FILE_TYPE.EXTRACTION_REVIEW;

  /** ------------------------------
   * Fetch Reports when Tab changes
   --------------------------------*/
  useEffect(() => {
    const currentType = tabItems[activeTab].type;

    if (currentType === FILE_TYPE.EXTRACTION_REVIEW) {
      setReview(2); // Permit Compliance Verification
      getReportsExtraction({
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: 1,
        exclude_status: 0,
      });
    } else {
      setReview(3); // legal review
      getReports({
        type: currentType,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: 1,
      });
    }

    setPageNo(1);
  }, [activeTab]);

  /** ------------------------------
   * Pagination Handler - FIXED
   --------------------------------*/
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageNo(newPage);

      const currentType = tabItems[activeTab].type;

      if (currentType === FILE_TYPE.EXTRACTION_REVIEW) {
        getReportsExtraction({
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          page: newPage,
          exclude_status: 0,
        });
      } else {
        getReports({
          type: currentType,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          page: newPage,
        });
      }
    },
    [activeTab, tabItems, getReports, getReportsExtraction]
  );

  /** ------------------------------
   * Get correct data based on active tab
   --------------------------------*/
  const currentData = useMemo(() => {
    if (isExtractionTab) {
      return {
        reportsData: reportsExtraction || [],
        totalItems: reportCountExtraction,
        loading: extractionReportsLoading,
        currentPage: page,
      };
    }
    return {
      reportsData: reports?.data || reports?.rows || [],
      totalItems: reportCount,
      loading: reportsLoading,
      currentPage: reports?.pagination?.currentPage || page,
    };
  }, [
    isExtractionTab,
    reportsExtraction,
    reports,
    page,
    reportsLoading,
    extractionReportsLoading,
    reportCount,
    reportCountExtraction,
  ]);

  const scrollbarStyles = {
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#D1D5DB",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#9CA3AF",
    },
  };

  const ReportContent = ({ tabIndex }: { tabIndex: number }) => {
    const isActiveTab = tabIndex === activeTab;
    const isCurrentTabExtraction =
      tabItems[tabIndex].type === FILE_TYPE.EXTRACTION_REVIEW;
    const isLoading = isCurrentTabExtraction
      ? extractionReportsLoading
      : reportsLoading;

    if (isLoading && isActiveTab) {
      return (
        <Center p={8} flex={1}>
          <Spinner size="lg" color="blue.500" thickness="4px" />
        </Center>
      );
    }

    const isEmpty =
      !currentData.reportsData || currentData?.reportsData?.length === 0;

    return (
      <>
        <Box flex={1} overflowY="auto" minHeight={0} css={scrollbarStyles}>
          {isEmpty ? (
            <Center h="100%">
              <Text color="gray.500" fontSize="lg">
                {t("translate.noDataFound")}
              </Text>
            </Center>
          ) : (
            <ReportList
              reports={currentData.reportsData}
              count={currentData.totalItems}
              onPageChange={handlePageChange}
              page={currentData.currentPage}
              showPagination={false}
              isExtraction={isExtractionTab}
            />
          )}
        </Box>
        <Flex flexShrink={0} justifyContent="center" mt={4}>
          <Paginate
            page={page}
            count={currentData.totalItems}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={handlePageChange}
          />
        </Flex>
      </>
    );
  };

  return (
    <Box maxW="100%" h="100%" display="flex" flexDirection="column">
      <Tabs
        variant="unstyled"
        index={activeTab}
        onChange={setActiveTab}
        display="flex"
        flexDirection="column"
        h="100%"
      >
        <TabList gap={3} flexShrink={0}>
          {tabItems.map((tab, index) => (
            <Tab
              key={index}
              display="flex"
              alignItems="center"
              gap={2}
              borderRadius="md"
              fontWeight="medium"
              bg="#EBEBEB"
              color="black"
              transition="all 0.2s"
              _selected={{
                bg: "black",
                color: "white",
              }}
            >
              <Icon as={tab.icon} boxSize={6} />
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels
          mt={5}
          p={0}
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
        >
          {tabItems.map((_, index) => (
            <TabPanel
              key={index}
              p={0}
              h="100%"
              display="flex"
              flexDirection="column"
            >
              <ReportContent tabIndex={index} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ReportSection;
